import React, { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { subject } from "@casl/ability";
import { deleteReq, postReq } from "../../utils/request";
import { PageName, PermissionAction } from "../../types/Authorization";
import { lookupEntryNoun } from "../LookupMenu";
import { useAbilities } from "../../utils/ability";
import { usePagePermission } from "../../hooks/usePagePermission";
import { showDuplicateError } from "../../utils/toastHelper";
import { useConfirm } from "../../contexts/ConfirmContext";
import Chip from "../Chip";
import { RecordAction } from "./RecordActions";

// Lazy: LogsModal pulls in Table → TimeZoneDisplay → react-clock, which every
// page (and Jest test) importing the record kit would otherwise load up front.
const LogsModal = React.lazy(() => import("../LogsModal"));

export interface RecordLifecycleOptions {
    /** The CASL page the record belongs to (PageName.*). */
    pageName: string;
    /** What one record is called in confirms and toasts ("provider"). Defaults from the page name. */
    noun?: string;
    /** The record's name, so a confirm says which one ("Sentinel Security UAE"). */
    recordName?: string | null;
    /** The record's creator — drives the Own / Team permission levels. */
    createdBy: number | null | undefined;
    /** False for a soft-deleted record. */
    isActive: boolean;
    /** Called after a successful delete (false) or restore (true). */
    onSetActive: (active: boolean) => void;
    deleteUrl?: string;
    restoreUrl?: string;
    logsUrl?: string;
    /** The create page — "New" reloads into it so no state leaks from this record. */
    newUrl?: string;
    /** "New user", "New team"… (default "New"). */
    newLabel?: string;
    /** False on a create page — no record yet, so no lifecycle actions (default true). */
    isEditMode?: boolean;
    /** A record that cannot be changed (e.g. a posted document): no Delete unless `allowDeleteWhenReadOnly`. */
    forceReadOnly?: boolean;
    allowDeleteWhenReadOnly?: boolean;
}

export interface RecordLifecycle {
    /** Change log, New and Delete, each only when the user may — put them under "More". */
    menuActions: RecordAction[];
    /** A visible secondary action while the record is inactive and the user may restore it. */
    restoreAction?: RecordAction;
    /** Opens the change log (for a RecordFooter "Change log" link). Undefined when the user can't see logs. */
    openLogs?: () => void;
    /** Render once on the page — the change log modal. */
    logsModal: React.ReactNode;
    /** Edit permission on this record, same rule as DetailsActionButtons (record level or page level). */
    canEdit: boolean;
    canDelete: boolean;
    canRestore: boolean;
    canCreate: boolean;
    canViewLogs: boolean;
    isDeleting: boolean;
    isRestoring: boolean;
    /** Runs the confirm + delete flow (for an attention chip like "Delete to complete the request"). */
    handleDelete: () => Promise<void>;
}

/** The neutral header chip for a soft-deleted record. */
export const RecordInactiveChip: React.FC<{ noun?: string }> = ({ noun = "record" }) => (
    <Chip icon="archive" title={`This ${noun} is deleted (inactive) — Restore brings it back`}>Inactive</Chip>
);

/**
 * Delete / Restore / Change log / New for a record page's header, lifted
 * from DetailsActionButtons with the same rules: CASL checks against the
 * record's creator, the page-level edit umbrella, confirm dialogs, and the
 * delete/restore toasts that flip the page's active state. Anything the user
 * may not do is left out, never shown disabled.
 */
const useRecordLifecycle = ({
    pageName, noun: nounOverride, recordName, createdBy, isActive, onSetActive, deleteUrl, restoreUrl, logsUrl, newUrl, newLabel = "New",
    isEditMode = true, forceReadOnly = false, allowDeleteWhenReadOnly = false,
}: RecordLifecycleOptions): RecordLifecycle => {
    const confirm = useConfirm();
    // "Journal entries" → "journal entry"; a page whose name isn't a record kind says "record".
    const noun = nounOverride ?? (pageName === PageName.CONFIGS ? "record" : lookupEntryNoun(null, pageName));
    const Noun = noun.charAt(0).toUpperCase() + noun.slice(1);
    const name = recordName?.trim() ?? "";
    const abilities = useAbilities(pageName);
    // CASL's Team-level matcher is tighter than the BE's visibility model; if
    // the BE served the record, page-level edit is the right umbrella (same as
    // DetailsActionButtons). Field locks still apply server-side.
    const pageLevelEdit = usePagePermission(pageName, PermissionAction.EDIT);
    const [canDelete, setCanDelete] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canRestore, setCanRestore] = useState(false);
    const [canCreate, setCanCreate] = useState(false);
    const [canViewLogs, setCanViewLogs] = useState(false);
    const [isLogsOpen, setIsLogsOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isRestoring, setIsRestoring] = useState(false);

    useEffect(() => {
        if (abilities) {
            const subjectWithCreatedBy = subject(pageName, { createdBy: createdBy ?? 0 });
            setCanDelete(abilities.can(PermissionAction.DELETE, subjectWithCreatedBy));
            setCanRestore(abilities.can(PermissionAction.RESTORE, subjectWithCreatedBy));
            setCanEdit(abilities.can(PermissionAction.EDIT, subjectWithCreatedBy) || pageLevelEdit);
            setCanCreate(abilities.can(PermissionAction.CREATE, subjectWithCreatedBy));
            setCanViewLogs(abilities.can(PermissionAction.LOGS, subjectWithCreatedBy));
        }
    }, [abilities, pageName, createdBy, pageLevelEdit]);

    const handleDelete = useCallback(async () => {
        if (!deleteUrl || isDeleting) return;
        const confirmDelete = await confirm({
            danger: true,
            entity: noun,
            message: <>Delete {noun}{name && <> <strong>{name}</strong></>}? It becomes inactive{restoreUrl ? ", and Restore brings it back" : ""}.</>,
        });
        if (!confirmDelete) return;
        setIsDeleting(true);
        try {
            const response = await deleteReq(deleteUrl);
            if (response && response.status === 200) {
                toast.success(`${Noun} deleted`, {
                    autoClose: 1000,
                    onClose: () => onSetActive(false),
                });
            } else {
                showDuplicateError(response?.body, `Couldn't delete the ${noun}. Contact an admin if this keeps happening.`);
            }
        } catch (error) {
            toast.error(`Couldn't delete the ${noun}. Contact an admin if this keeps happening.`);
        } finally {
            setIsDeleting(false);
        }
    }, [confirm, deleteUrl, restoreUrl, isDeleting, onSetActive, noun, Noun, name]);

    const handleRestore = useCallback(async () => {
        if (!restoreUrl || isRestoring) return;
        const confirmRestore = await confirm({
            entity: noun,
            titleVerb: "Restore",
            titleIcon: "bi-arrow-repeat",
            confirmLabel: "Restore",
            message: <>Restore {noun}{name && <> <strong>{name}</strong></>}? It becomes active again.</>,
        });
        if (!confirmRestore) return;
        setIsRestoring(true);
        try {
            const response = await postReq(restoreUrl, {});
            if (response && response.status === 200) {
                toast.success(`${Noun} restored`, {
                    autoClose: 1000,
                    onClose: () => onSetActive(true),
                });
            } else {
                showDuplicateError(response?.body, `Couldn't restore the ${noun}. Contact an admin if this keeps happening.`);
            }
        } catch (error) {
            toast.error(`Couldn't restore the ${noun}. Contact an admin if this keeps happening.`);
        } finally {
            setIsRestoring(false);
        }
    }, [confirm, restoreUrl, isRestoring, onSetActive, noun, Noun, name]);

    const showLogs = canViewLogs && isEditMode && !!logsUrl;
    const showNew = !!newUrl && isEditMode && canCreate;
    const showDelete = isEditMode && isActive && !!deleteUrl && canDelete && (!forceReadOnly || allowDeleteWhenReadOnly);
    const showRestore = isEditMode && !isActive && !!restoreUrl && canRestore;

    const openLogs = useCallback(() => setIsLogsOpen(true), []);

    const menuActions = useMemo<RecordAction[]>(() => {
        const actions: RecordAction[] = [];
        if (showLogs) {
            actions.push({ key: "logs", label: "Change log", title: `Every change made to this ${noun}, who made it and when`, icon: "clock-history", inMenu: true, onClick: openLogs });
        }
        if (showNew) {
            actions.push({
                key: "new",
                label: newLabel,
                title: `Start a new ${noun}`,
                icon: "plus-lg",
                inMenu: true,
                onClick: () => { window.location.href = newUrl as string; },
            });
        }
        if (showDelete) {
            actions.push({
                key: "delete",
                label: "Delete",
                title: `Delete this ${noun} — it becomes inactive and can be restored later`,
                icon: "trash",
                variant: "danger",
                inMenu: true,
                loading: isDeleting,
                onClick: handleDelete,
            });
        }
        return actions;
    }, [showLogs, showNew, showDelete, newLabel, newUrl, isDeleting, openLogs, handleDelete, noun]);

    const restoreAction: RecordAction | undefined = showRestore
        ? {
            key: "restore",
            label: "Restore",
            title: `Bring this deleted ${noun} back — it becomes active again`,
            icon: "arrow-counterclockwise",
            loading: isRestoring,
            onClick: handleRestore,
        }
        : undefined;

    const logsModal = logsUrl && isLogsOpen
        ? (
            <Suspense fallback={null}>
                <LogsModal isOpen onClose={() => setIsLogsOpen(false)} logsURL={logsUrl} />
            </Suspense>
        )
        : null;

    return {
        menuActions,
        restoreAction,
        openLogs: showLogs ? openLogs : undefined,
        logsModal,
        canEdit,
        canDelete,
        canRestore,
        canCreate,
        canViewLogs,
        isDeleting,
        isRestoring,
        handleDelete,
    };
};

export default useRecordLifecycle;
